import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, TextField, Typography } from '@mui/material';
import { registration } from '../services/api';
import { apiError } from '../utils/format';

const CODE_LENGTH = 6;
const RESEND_WAIT = 60; // seconds, as on the server

// Confirming the email with the six-digit code from the letter (sent at sign-up, valid 15 minutes).
// Until it is done only «Главная» and «Настройки» work. A new code can be asked for once a minute;
// the server keeps the count, the card only shows the wait it reports.
const EmailConfirmCard = ({ email, onVerified }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const timer = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);

  const verify = async (value) => {
    setBusy(true);
    setError('');
    setInfo('');
    try {
      await registration.verifyCode(value);
      onVerified();
    } catch (err) {
      setError(apiError(err, 'Не удалось проверить код'));
      setCode('');
      setBusy(false);
    }
  };

  const change = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(value);
    if (value.length === CODE_LENGTH && !busy) verify(value);
  };

  const submit = (e) => {
    e.preventDefault();
    if (code.length === CODE_LENGTH && !busy) verify(code);
  };

  const resend = async () => {
    setError('');
    setInfo('');
    try {
      await registration.resendCode();
      setInfo(`Новый код отправлен на ${email}. Проверьте также папку «Спам».`);
      setWait(RESEND_WAIT);
    } catch (err) {
      const seconds = err.response?.data?.retry_after;
      if (seconds) setWait(seconds);
      setError(apiError(err, 'Не удалось отправить код'));
    }
  };

  return (
    <Card id="email-confirm" sx={{ border: '2px solid #F5B14C' }}>
      <CardContent component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560 }}>
        <Typography variant="h3">Подтвердите почту</Typography>
        <Typography color="text.secondary">
          Мы отправили код на <b>{email}</b> при регистрации. Введите его, чтобы открыть разделы сервиса. Код действует 15&nbsp;минут;
          если он не пришёл или устарел, отправьте новый.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {info && <Alert severity="success">{info}</Alert>}
        <TextField
          fullWidth
          label="Код из письма"
          value={code}
          onChange={change}
          disabled={busy}
          autoComplete="one-time-code"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: CODE_LENGTH, 'aria-label': 'Код из письма',
            style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em', fontWeight: 700 } }}
        />
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button type="submit" variant="contained" disabled={busy || code.length !== CODE_LENGTH}>
            {busy ? 'Проверка…' : 'Подтвердить'}
          </Button>
          <Button onClick={resend} disabled={wait > 0 || busy}>
            {wait > 0 ? `Отправить код ещё раз через ${wait} с` : 'Отправить код ещё раз'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmailConfirmCard;
